import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { useNotification } from '../context/NotificationContext';
//import './Notification.css'; // ✅ new CSS file for Notification styles

const BASE_URL = process.env.REACT_APP_BASE_URL;

const CheckIcon = ({ title }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 20 20"
    fill="none"
    aria-label={title}
  >
    <path
      d="M7.629 15.314a1 1 0 0 1-1.414 0l-3.536-3.536a1 1 0 1 1 1.414-1.414l2.829 2.828 7.778-7.778a1 1 0 1 1 1.414 1.414l-8.485 8.486z"
      fill="var(--primary-color)"
    />
  </svg>
);

const TrashIcon = ({ title }) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 20 20"
    fill="none"
    aria-label={title}
  >
    <path
      d="M7 8a1 1 0 0 1 2 0v5a1 1 0 1 1-2 0V8zm4 0a1 1 0 0 1 2 0v5a1 1 0 1 1-2 0V8z"
      fill="#e74c3c"
    />
    <path
      d="M5 6V5a3 3 0 0 1 3-3h4a3 3 0 0 1 3 3v1h3a1 1 0 1 1 0 2h-1v9a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V8H2a1 1 0 1 1 0-2h3zm2-1v1h6V5a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1zm8 3H5v9a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V8z"
      fill="#e74c3c"
    />
  </svg>
);

const Notification = () => {
  const [notifications, setNotifications] = useState([]);
  const [selected, setSelected] = useState([]);
  const user = JSON.parse(localStorage.getItem('user'));
  const token = localStorage.getItem('token');

  const roleBasedId = (() => {
    const role = user?.role_id;
    if (role === 1) return localStorage.getItem('admin_id');
    if (role === 2) return localStorage.getItem('doctor_id');
    if (role === 3) return localStorage.getItem('receptionist_id');
    if (role === 4) return localStorage.getItem('patient_id');
    if (role === 5) return localStorage.getItem('lab_id');
    return null;
  })();

  const { updateUnreadCount } = useNotification();

  useEffect(() => {
    if (!user || !token) return;
    axios
      .get(`${BASE_URL}/api/notification/getNotifications`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          user_id: roleBasedId,
          user_type:
            user.role_id === 1
              ? 'admin'
              : user.role_id === 2
              ? 'doctor'
              : user.role_id === 3
              ? 'receptionist'
              : user.role_id === 4
              ? 'patient'
              : user.role_id === 5
              ? 'lab'
              : '',
          hospital_id: user.hospital_id,
          sub_hospital_id: user.sub_hospital_id,
        },
      })
      .then((res) => {
        setNotifications(res.data);
        const unread = res.data.filter((n) => n.is_read === 0).length;
        updateUnreadCount(unread);
      })
      .catch(() => {
        setNotifications([]);
        updateUnreadCount(0);
      });
  }, [user, token, updateUnreadCount]);

  const markAsRead = async (id) => {
    try {
      await axios.put(
        `${BASE_URL}/api/notification/markAsRead/${id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n))
      );
      updateUnreadCount((prev) => (prev > 0 ? prev - 1 : 0));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const deleteNotification = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/api/notification/delete/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => {
        const updated = prev.filter((n) => n.id !== id);
        const unread = updated.filter((n) => n.is_read === 0).length;
        updateUnreadCount(unread);
        return updated;
      });
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const allSelected =
    notifications.length > 0 && selected.length === notifications.length;
  const someSelected =
    selected.length > 0 && selected.length < notifications.length;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(notifications.map((n) => n.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const deleteSelectedNotifications = async () => {
    try {
      await Promise.all(
        selected.map((id) =>
          axios.delete(`${BASE_URL}/api/notification/delete/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
        )
      );
      setNotifications((prev) => {
        const updated = prev.filter((n) => !selected.includes(n.id));
        const unread = updated.filter((n) => n.is_read === 0).length;
        updateUnreadCount(unread);
        return updated;
      });
      setSelected([]);
    } catch (err) {
      console.error('Failed to delete selected notifications:', err);
    }
  };

  return (
    <div className="d-flex">
      <Sidebar className="d-none d-md-block" />
      <div className="notification-page">
        <div className="notification-list">
          <div className="notification-list-header">
            <h2 className="global-heading">Notifications</h2>
            {selected.length > 0 && (
              <button
                className="notification-action-btn"
                onClick={deleteSelectedNotifications}
                type="button"
              >
                <TrashIcon title="Delete Selected" />
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <div className="no-notification">No notifications found.</div>
          ) : (
            <ul className="notification-ul">
              <li className="notification-selectall-row">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={handleSelectAll}
                />
                <span>Select All</span>
              </li>
              {notifications.map((n) => (
                <li
                  key={n.id}
                  className={`notification-item ${
                    n.is_read ? 'notification-read' : ''
                  }`}
                >
                  <div className="notification-header">
                    <div className="notification-title-wrap">
                      <input
                        type="checkbox"
                        checked={selected.includes(n.id)}
                        onChange={() => handleSelectOne(n.id)}
                      />
                      <div className="notification-title">{n.title}</div>
                    </div>
                    <div className="notification-actions">
                      {!n.is_read && (
                        <button
                          className="notification-action-btn"
                          onClick={() => markAsRead(n.id)}
                          type="button"
                        >
                          <CheckIcon title="Mark as Read" />
                        </button>
                      )}
                      <button
                        className="notification-action-btn"
                        onClick={() => deleteNotification(n.id)}
                        type="button"
                      >
                        <TrashIcon title="Delete" />
                      </button>
                    </div>
                  </div>
                  <div className="notification-message">{n.message}</div>
                  <div className="notification-date">
                    {new Date(n.created_at).toLocaleString()}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Notification;
